<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { Close } from '@element-plus/icons-vue'
import { useChatStore } from '/src/stores/chat'
import ChatTabs from './ChatTabs.vue'
import ChatsView from './ChatsView.vue'
import ContactsView from './ContactsView.vue'
import MeetingsView from './MeetingsView.vue'
import TasksView from './TasksView.vue'

const chatStore = useChatStore()

const MIN_WIDTH = 300
const MAX_WIDTH = 480
const MIN_HEIGHT = 400
const MOBILE_BREAKPOINT = 768

const panelRef = ref(null)
const isDragging = ref(false)
const isResizing = ref(false)
const resizeDirection = ref('')
const dragStart = ref({ x: 0, y: 0 })
const startPos = ref({ x: 0, y: 0 })
const startSize = ref({ width: 0, height: 0 })

const isMobile = computed(() => {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
})

const panelStyle = computed(() => {
  if (isMobile.value) {
    return {
      left: '0',
      top: '0',
      width: '100%',
      height: '100%',
      borderRadius: '0'
    }
  }
  return {
    left: `${chatStore.panelPosition.x}px`,
    top: `${chatStore.panelPosition.y}px`,
    width: `${chatStore.panelSize.width}px`,
    height: `${chatStore.panelSize.height}px`
  }
})

const headerStyle = computed(() => ({
  cursor: isDragging.value ? 'grabbing' : 'grab'
}))

const handleHeaderMouseDown = (e) => {
  if (isMobile.value) return
  if (e.target.closest('.panel-controls')) return
  if (e.button !== 0) return

  isDragging.value = true
  dragStart.value = { x: e.clientX, y: e.clientY }
  startPos.value = { ...chatStore.panelPosition }

  document.addEventListener('mousemove', handleHeaderMouseMove)
  document.addEventListener('mouseup', handleHeaderMouseUp)
}

const handleHeaderMouseMove = (e) => {
  if (!isDragging.value) return

  const deltaX = e.clientX - dragStart.value.x
  const deltaY = e.clientY - dragStart.value.y

  let newX = startPos.value.x + deltaX
  let newY = startPos.value.y + deltaY

  newX = Math.max(0, Math.min(window.innerWidth - chatStore.panelSize.width, newX))
  newY = Math.max(0, Math.min(window.innerHeight - 48, newY))

  chatStore.setPanelPosition({ x: newX, y: newY })
}

const handleHeaderMouseUp = () => {
  isDragging.value = false
  document.removeEventListener('mousemove', handleHeaderMouseMove)
  document.removeEventListener('mouseup', handleHeaderMouseUp)
}

const handleResizeStart = (direction, e) => {
  if (isMobile.value) return
  if (e.button !== 0) return

  isResizing.value = true
  resizeDirection.value = direction
  dragStart.value = { x: e.clientX, y: e.clientY }
  startPos.value = { ...chatStore.panelPosition }
  startSize.value = { ...chatStore.panelSize }

  document.addEventListener('mousemove', handleResizeMove)
  document.addEventListener('mouseup', handleResizeEnd)
}

const handleResizeMove = (e) => {
  if (!isResizing.value) return

  const deltaX = e.clientX - dragStart.value.x
  const deltaY = e.clientY - dragStart.value.y

  let newWidth = startSize.value.width
  let newHeight = startSize.value.height
  let newX = startPos.value.x
  let newY = startPos.value.y

  if (resizeDirection.value.includes('e')) {
    newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startSize.value.width + deltaX))
  }
  if (resizeDirection.value.includes('s')) {
    newHeight = Math.max(MIN_HEIGHT, Math.min(window.innerHeight - chatStore.panelPosition.y - 60, startSize.value.height + deltaY))
  }
  if (resizeDirection.value.includes('w')) {
    newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startSize.value.width - deltaX))
    newX = startPos.value.x + (startSize.value.width - newWidth)
  }
  if (resizeDirection.value.includes('n')) {
    newHeight = Math.max(MIN_HEIGHT, Math.min(startSize.value.height - deltaY))
    newY = startPos.value.y + (startSize.value.height - newHeight)
  }

  chatStore.setPanelPosition({ x: newX, y: newY })
  chatStore.setPanelSize({ width: newWidth, height: newHeight })
}

const handleResizeEnd = () => {
  isResizing.value = false
  resizeDirection.value = ''
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
}

const handleClose = () => {
  chatStore.closePanel()
}

const handleKeyDown = (e) => {
  if (e.key === 'Escape' && chatStore.isExpanded) {
    chatStore.closePanel()
  }
}

const currentView = computed(() => {
  const views = {
    chats: ChatsView,
    contacts: ContactsView,
    meetings: MeetingsView,
    tasks: TasksView
  }
  return views[chatStore.activeTab] || ChatsView
})

onMounted(() => {
  chatStore.loadPanelSize()
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', handleHeaderMouseMove)
  document.removeEventListener('mouseup', handleHeaderMouseUp)
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div
    ref="panelRef"
    class="chat-panel"
    :style="panelStyle"
    role="dialog"
    aria-label="消息面板"
    :aria-hidden="!chatStore.isExpanded"
  >
    <div
      class="resize-handle resize-handle-n"
      @mousedown="handleResizeStart('n', $event)"
    />

    <div
      class="panel-header"
      :style="headerStyle"
      @mousedown="handleHeaderMouseDown"
    >
      <div class="drag-indicator">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div class="panel-title">
        <el-icon><Message /></el-icon>
        <span>消息</span>
      </div>
      <div class="panel-controls">
        <el-button
          text
          circle
          size="small"
          @click="handleClose"
        >
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
    </div>

    <ChatTabs />

    <div class="panel-content">
      <component :is="currentView" />
    </div>

    <div
      class="resize-handle resize-handle-s"
      @mousedown="handleResizeStart('s', $event)"
    />
    <div
      class="resize-handle resize-handle-e"
      @mousedown="handleResizeStart('e', $event)"
    />
    <div
      class="resize-handle resize-handle-w"
      @mousedown="handleResizeStart('w', $event)"
    />
  </div>
</template>

<style scoped>
.chat-panel {
  position: fixed;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.panel-header {
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 12px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  user-select: none;
}

.drag-indicator {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-right: 12px;
  padding: 4px;
}

.drag-indicator span {
  display: block;
  width: 16px;
  height: 2px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 1px;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  font-size: 16px;
  font-weight: 600;
}

.panel-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.panel-controls .el-button {
  color: white;
}

.panel-controls .el-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.panel-content {
  flex: 1;
  overflow: hidden;
  background: #f9fafb;
}

.resize-handle {
  position: absolute;
  z-index: 10;
}

.resize-handle-n,
.resize-handle-s {
  left: 0;
  right: 0;
  height: 6px;
  cursor: ns-resize;
}

.resize-handle-n {
  top: 0;
}

.resize-handle-s {
  bottom: 0;
}

.resize-handle-e,
.resize-handle-w {
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
}

.resize-handle-e {
  right: 0;
}

.resize-handle-w {
  left: 0;
}

.resize-handle:hover {
  background: rgba(14, 165, 233, 0.2);
}
</style>
